import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useGraphQLQuery from './useGraphQLQuery';
import { useGuaranteeProduct } from './useGuaranteePurchases';
import { clearSearch, setSearch } from '../data/searchField/actions';
import { setRefreshSearch } from '../data/refreshSearch/actions';
import { DEFAULT_PAGE_SIZE } from '../constants';

const FIRST_PAGE = 1;

const selectRefreshSearch = state => state.data.refreshSearch;

const useExternalPurchasesList = ({
  query,
  responseField,
  buildVariables,
  dealerField = 'dealerNumbers',
}) => {
  const dispatch = useDispatch();
  const [selectedProduct] = useGuaranteeProduct();
  const refreshSearch = useSelector(selectRefreshSearch);
  const [queryState, setQueryState] = useState({
    page: FIRST_PAGE,
    vin: null,
    dealer: null,
  });
  const [productContext, setProductContext] = useState(selectedProduct);
  if (productContext !== selectedProduct) {
    setProductContext(selectedProduct);
    setQueryState(state => ({ ...state, page: FIRST_PAGE }));
  }

  useEffect(() => {
    dispatch(clearSearch());
  }, [dispatch]);

  useEffect(() => {
    if (refreshSearch) dispatch(setRefreshSearch(false));
  }, [refreshSearch, dispatch]);

  const { page, vin, dealer } = queryState;

  const {
    data, loading, error, refetch,
  } = useGraphQLQuery({
    query,
    variables: buildVariables({
      page,
      categoryCode: selectedProduct || null,
      vin,
      dealer,
    }),
  });

  const onSearchSubmit = useCallback(
    event => {
      const { elements } = event.target;
      const vinField = elements.vin;
      const dealerInput = elements[dealerField];
      const vinValue = ((vinField && vinField.value) || '').trim() || null;
      const dealerValue = ((dealerInput && dealerInput.value) || '').trim() || null;
      const dealerSearchField = dealerField === 'dealerNumbers' && dealerValue
        ? [dealerValue]
        : dealerValue;

      setQueryState({ page: FIRST_PAGE, vin: vinValue, dealer: dealerValue });
      dispatch(setSearch({ vin: vinValue, [dealerField]: dealerSearchField }));
    },
    [dispatch, dealerField]
  );

  const response = data && data[responseField];
  const items = (response && response.items) || [];
  const total = (response && response.total) || 0;
  const start = items.length > 0 ? (page - 1) * DEFAULT_PAGE_SIZE + 1 : 0;
  const end = start > 0 ? start + items.length - 1 : 0;
  const hasNext = end < total;
  const isLastPage = !hasNext && items.length !== 0;
  const isInitialLoading = loading && items.length === 0;
  const isPaginationLoading = loading && !isInitialLoading;

  const nextHandler = useCallback(() => {
    if (hasNext) setQueryState(state => ({ ...state, page: state.page + 1 }));
  }, [hasNext]);

  const previousHandler = useCallback(() => {
    setQueryState(state => ({
      ...state,
      page: Math.max(FIRST_PAGE, state.page - 1),
    }));
  }, []);

  const refreshSilently = useCallback(() => refetch({ silent: true }), [refetch]);

  return {
    selectedProduct,
    items,
    total,
    loading,
    error,
    refetch,
    refreshSilently,
    start,
    end,
    isLastPage,
    isInitialLoading,
    isPaginationLoading,
    nextHandler,
    previousHandler,
    onSearchSubmit,
    page,
  };
};

export default useExternalPurchasesList;
